/**
 * ActivityIndicator - the orchestrator for the chat Activity_Indicator.
 *
 * This component composes the three presentational layers for a single
 * assistant message, choosing which to render based on the message's streaming
 * state and its Activity_Trace. It owns no activity logic of its own — ordering,
 * live-activity selection, and presentation labels all live in the pure helpers
 * and in the layer components it delegates to.
 *
 * Behavior (per the design's three-layer treatment):
 *   - Zero activities: render nothing (Requirements 8.3, 11.5, 11.6).
 *   - While the message is still streaming: render exactly one
 *     {@link LiveActivityStatus}, the live status line for the
 *     most-recently-started in-progress activity. It clears itself when every
 *     activity reaches a terminal status (Requirement 7.3) and, because this
 *     layer stops rendering once streaming ends, no in-progress label remains
 *     when the stream terminates (Requirements 7.1, 7.4).
 *   - Once generation is complete (not streaming) with one or more activities:
 *     render the collapsed {@link ActivityChips} summary plus the expandable
 *     {@link ActivityTrace} disclosure (Requirements 8.1, 9.1).
 */
import { clsx } from 'clsx';
import type { ChatMessageData } from '@/types/chat';
import { LiveActivityStatus } from './LiveActivityStatus';
import { ActivityChips } from './ActivityChips';
import { ActivityTrace } from './ActivityTrace';

export interface ActivityIndicatorProps {
  /** The assistant message whose Activity_Trace is being presented. */
  message: ChatMessageData;
  /** Optional extra classes for layout. */
  className?: string;
}

/**
 * Orchestrate the live status line, collapsed chips, and expandable trace for
 * one assistant message.
 */
export function ActivityIndicator({ message, className }: ActivityIndicatorProps) {
  const activities = message.activities ?? [];

  // Requirements 8.3, 11.5, 11.6: nothing to show when the message performed no
  // activity.
  if (activities.length === 0) {
    return null;
  }

  // Requirements 7.1, 7.3, 7.4: while streaming, show only the live status line.
  // LiveActivityStatus renders nothing when no activity is in progress, so the
  // line clears as soon as every activity is terminal.
  if (message.isStreaming) {
    return (
      <div className={clsx('mt-1', className)} data-testid="activity-indicator">
        <LiveActivityStatus activities={activities} />
      </div>
    );
  }

  // Requirements 8.1, 9.1: once generation is complete, show the collapsed
  // summary plus the expandable trace.
  return (
    <div
      className={clsx('mt-1 space-y-1.5', className)}
      data-testid="activity-indicator"
    >
      <ActivityChips activities={activities} />
      <ActivityTrace activities={activities} />
    </div>
  );
}
